import * as amplitude from "@amplitude/analytics-browser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { getDropoffQrCodeByProvider } from "@redotech/redo-return-core-common/dropoff/utils";
import {
  MerchantAppReturn,
  Return,
} from "@redotech/redo-return-core-common/return";
import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  dropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { toast } from "@redotech/redo-web/alert";
import { assertNever } from "@redotech/util/type";
import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";

function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return t(
    "Failed to send email. Please contact our support team to resolve this issue.",
  );
}

/**
 * Whether a dropoff provider stores its QR code in the new
 * `dropoffs.qrCodes` array (as opposed to a legacy provider-specific field).
 * When the answer is yes, the presence of a QR code on the return means it
 * should route through the external-return-shipping-provider RPC. Adding a
 * new provider forces a decision via the exhaustive switch.
 */
function hasNewSchemaExternalShippingQrCode(
  return_: Return | MerchantAppReturn,
  provider: DropoffProviderType,
): boolean {
  switch (provider) {
    case BLUE_YONDER_PROVIDER:
    case VIRTUAL_PROVIDER:
    case HAPPY_RETURNS_PROVIDER:
      return !!getDropoffQrCodeByProvider(return_, provider);
    case RETURN_BEAR_PROVIDER:
      // ReturnBear does not store QR codes in `dropoffs.qrCodes`.
      return false;
    default:
      assertNever(provider);
  }
}

export interface ResendLabelVariables {
  emailOverride: string;
}

export function useResendLabel(return_: Return | MerchantAppReturn) {
  const { t } = useTranslation();
  const merchantRpcClient = useContext(RedoMerchantRpcClientContext);
  const teamContext = useContext(TeamContext);

  return useMutation({
    mutationFn: async ({ emailOverride }: ResendLabelVariables) => {
      try {
        // The dropoffs.qrCodes path iterates the provider enum and dispatches
        // through a switch so a new provider is enforced at compile time.
        const isExternalReturnShippingProvider = dropoffProviderType.some(
          (provider) => hasNewSchemaExternalShippingQrCode(return_, provider),
        );

        amplitude.logEvent(
          isExternalReturnShippingProvider
            ? "resend-external-return-shipping-provider-label"
            : "resend-label-support",
          { team: teamContext?._id, returnId: return_._id },
        );

        if (isExternalReturnShippingProvider && merchantRpcClient) {
          const result =
            await merchantRpcClient.resendExternalReturnShippingProviderLabel({
              returnId: return_._id,
              emailOverride: emailOverride || undefined,
            });

          if (!result.success) {
            throw new Error(
              result.message || t("Failed to send shipping label email"),
            );
          }
          return;
        }

        if (!merchantRpcClient) {
          throw new Error(t("RPC client not available"));
        }

        const result = await merchantRpcClient.resendLabel({
          returnId: return_._id,
          emailOverride: emailOverride || undefined,
        });

        if (!result.success) {
          throw new Error(
            result.message || t("Failed to send shipping label email"),
          );
        }
      } catch (err) {
        throw new Error(getErrorMessage(err));
      }
    },
    onSuccess: () => {
      toast(t("Label sent successfully!"), { variant: "success" });
    },
    onError: (error) => {
      const message =
        error instanceof Error && error.message
          ? t("Resending label failed: {{errorMessage}}", {
              errorMessage: error.message,
            })
          : t("Resending label failed");
      toast(message, { variant: "error" });
    },
  });
}
